// src/utils/firebasePerformanceMonitor.js
import { Logger } from "./logger";
import {
  createOperationContext,
  FIREBASE_SERVICES,
  monitorFirebaseError,
} from "./firebaseErrorHandler";

/**
 * Firebase operation performance and health monitor
 */

/**
 * Operation metrics store
 */
const operationMetrics = {
  totalOperations: 0,
  failedOperations: 0,
  retryOperations: 0,
  fallbackOperations: 0,
  operationTimes: [],
  errorsByType: {},
  errorsByService: {},
  lastErrors: [],
};

/**
 * Circuit breaker states
 */
const CIRCUIT_BREAKER_STATES = {
  CLOSED: "closed", // Normal operation
  OPEN: "open", // Circuit is open, failing fast
  HALF_OPEN: "half_open", // Testing if service has recovered
};

/**
 * Circuit breaker configuration
 */
const CIRCUIT_BREAKER_CONFIG = {
  failureThreshold: 5, // Open circuit after 5 failures
  recoveryTimeout: 30000, // Wait 30 seconds before trying again
  successThreshold: 3, // Close circuit after 3 successes in half-open state
  monitoringWindow: 60000, // 1 minute sliding window
};

/**
 * Circuit breaker state for different Firebase services
 */
const circuitBreakers = {
  [FIREBASE_SERVICES.FIRESTORE]: {
    state: CIRCUIT_BREAKER_STATES.CLOSED,
    failures: 0,
    successes: 0,
    lastFailure: null,
    lastSuccess: null,
  },
  [FIREBASE_SERVICES.AUTH]: {
    state: CIRCUIT_BREAKER_STATES.CLOSED,
    failures: 0,
    successes: 0,
    lastFailure: null,
    lastSuccess: null,
  },
  [FIREBASE_SERVICES.STORAGE]: {
    state: CIRCUIT_BREAKER_STATES.CLOSED,
    failures: 0,
    successes: 0,
    lastFailure: null,
    lastSuccess: null,
  },
  [FIREBASE_SERVICES.FUNCTIONS]: {
    state: CIRCUIT_BREAKER_STATES.CLOSED,
    failures: 0,
    successes: 0,
    lastFailure: null,
    lastSuccess: null,
  },
};

/**
 * Performance monitor wrapper for Firebase operations
 */
export const monitorFirebaseOperation = async (
  operationName,
  firebaseOperation,
  context = {}
) => {
  const fullContext = createOperationContext(operationName, context);
  const service = context.service || FIREBASE_SERVICES.FIRESTORE;
  const startTime = Date.now();

  // Check circuit breaker
  if (shouldFailFast(service)) {
    const error = new Error(`Service ${service} is temporarily unavailable`);
    error.code = "circuit-breaker-open";

    Logger.log(
      "warn",
      "Circuit breaker is open, failing fast",
      {
        service,
        operationName,
        circuitState: circuitBreakers[service].state,
        sessionId: fullContext.sessionId,
      },
      "FirebasePerformanceMonitor"
    );

    throw error;
  }

  try {
    // Execute the operation
    const result = await firebaseOperation();
    const duration = Date.now() - startTime;

    // Record success metrics
    recordOperationSuccess(service, operationName, duration, fullContext);

    return result;
  } catch (error) {
    const duration = Date.now() - startTime;

    // Record failure metrics
    recordOperationFailure(
      service,
      operationName,
      error,
      duration,
      fullContext
    );

    // Monitor error for external services
    monitorFirebaseError(error, operationName, fullContext);

    throw error;
  }
};

/**
 * Check if circuit breaker should fail fast
 */
const shouldFailFast = (service) => {
  const breaker = circuitBreakers[service];

  switch (breaker.state) {
    case CIRCUIT_BREAKER_STATES.OPEN:
      // Check if recovery timeout has passed
      if (
        Date.now() - breaker.lastFailure >
        CIRCUIT_BREAKER_CONFIG.recoveryTimeout
      ) {
        breaker.state = CIRCUIT_BREAKER_STATES.HALF_OPEN;
        breaker.successes = 0;
        Logger.log(
          "info",
          "Circuit breaker transitioning to half-open",
          {
            service,
          },
          "FirebasePerformanceMonitor"
        );
        return false;
      }
      return true;

    case CIRCUIT_BREAKER_STATES.HALF_OPEN:
      return false;

    case CIRCUIT_BREAKER_STATES.CLOSED:
    default:
      return false;
  }
};

/**
 * Record successful operation
 */
const recordOperationSuccess = (service, operationName, duration, context) => {
  operationMetrics.totalOperations++;
  operationMetrics.operationTimes.push({ duration, timestamp: Date.now() });

  // Trim old operation times (keep last 100)
  if (operationMetrics.operationTimes.length > 100) {
    operationMetrics.operationTimes =
      operationMetrics.operationTimes.slice(-100);
  }

  // Update circuit breaker
  const breaker = circuitBreakers[service];
  breaker.lastSuccess = Date.now();

  if (breaker.state === CIRCUIT_BREAKER_STATES.HALF_OPEN) {
    breaker.successes++;
    if (breaker.successes >= CIRCUIT_BREAKER_CONFIG.successThreshold) {
      breaker.state = CIRCUIT_BREAKER_STATES.CLOSED;
      breaker.failures = 0;
      breaker.successes = 0;

      Logger.log(
        "info",
        "Circuit breaker closed - service recovered",
        {
          service,
          operationName,
        },
        "FirebasePerformanceMonitor"
      );
    }
  }

  Logger.log(
    "debug",
    "Firebase operation succeeded",
    {
      service,
      operationName,
      duration,
      sessionId: context.sessionId,
      circuitState: breaker.state,
    },
    "FirebasePerformanceMonitor"
  );
};

/**
 * Record failed operation
 */
const recordOperationFailure = (
  service,
  operationName,
  error,
  duration,
  context
) => {
  operationMetrics.totalOperations++;
  operationMetrics.failedOperations++;

  const errorCode = error?.code || "unknown";
  const baseErrorCode = errorCode
    .replace("firestore/", "")
    .replace("firebase/", "");

  // Track error types
  operationMetrics.errorsByType[baseErrorCode] =
    (operationMetrics.errorsByType[baseErrorCode] || 0) + 1;

  // Track errors by service
  operationMetrics.errorsByService[service] =
    (operationMetrics.errorsByService[service] || 0) + 1;

  // Keep last 10 errors for debugging
  operationMetrics.lastErrors.unshift({
    service,
    operationName,
    errorCode: baseErrorCode,
    message: error?.message,
    timestamp: Date.now(),
    duration,
    sessionId: context.sessionId,
  });

  if (operationMetrics.lastErrors.length > 10) {
    operationMetrics.lastErrors = operationMetrics.lastErrors.slice(0, 10);
  }

  // Update circuit breaker for certain types of errors
  if (isCircuitBreakerError(baseErrorCode)) {
    updateCircuitBreaker(service, operationName);
  }

  Logger.log(
    "error",
    "Firebase operation failed",
    {
      service,
      operationName,
      errorCode: baseErrorCode,
      duration,
      sessionId: context.sessionId,
      totalFailures: operationMetrics.failedOperations,
      failureRate:
        (
          (operationMetrics.failedOperations /
            operationMetrics.totalOperations) *
          100
        ).toFixed(2) + "%",
    },
    "FirebasePerformanceMonitor"
  );
};

/**
 * Check if error type should trigger circuit breaker
 */
const isCircuitBreakerError = (errorCode) => {
  const circuitBreakerErrors = [
    "unavailable",
    "deadline-exceeded",
    "resource-exhausted",
    "internal",
    "aborted",
  ];

  return circuitBreakerErrors.includes(errorCode);
};

/**
 * Update circuit breaker state
 */
const updateCircuitBreaker = (service, operationName) => {
  const breaker = circuitBreakers[service];
  breaker.failures++;
  breaker.lastFailure = Date.now();

  if (breaker.state === CIRCUIT_BREAKER_STATES.HALF_OPEN) {
    // Failed in half-open state, go back to open
    breaker.state = CIRCUIT_BREAKER_STATES.OPEN;
    breaker.successes = 0;

    Logger.log(
      "warn",
      "Circuit breaker opened from half-open state",
      {
        service,
        operationName,
        failures: breaker.failures,
      },
      "FirebasePerformanceMonitor"
    );
  } else if (
    breaker.state === CIRCUIT_BREAKER_STATES.CLOSED &&
    breaker.failures >= CIRCUIT_BREAKER_CONFIG.failureThreshold
  ) {
    // Too many failures, open circuit
    breaker.state = CIRCUIT_BREAKER_STATES.OPEN;

    Logger.log(
      "error",
      "Circuit breaker opened due to repeated failures",
      {
        service,
        operationName,
        failures: breaker.failures,
        threshold: CIRCUIT_BREAKER_CONFIG.failureThreshold,
      },
      "FirebasePerformanceMonitor"
    );
  }
};

/**
 * Get current performance metrics
 */
export const getPerformanceMetrics = () => {
  const now = Date.now();
  const recentOperations = operationMetrics.operationTimes.filter(
    (op) => now - op.timestamp < 60000 // Last minute
  );

  const avgResponseTime =
    recentOperations.length > 0
      ? recentOperations.reduce((sum, op) => sum + op.duration, 0) /
        recentOperations.length
      : 0;

  return {
    totalOperations: operationMetrics.totalOperations,
    failedOperations: operationMetrics.failedOperations,
    successRate:
      operationMetrics.totalOperations > 0
        ? (
            ((operationMetrics.totalOperations -
              operationMetrics.failedOperations) /
              operationMetrics.totalOperations) *
            100
          ).toFixed(2) + "%"
        : "N/A",
    averageResponseTime: Math.round(avgResponseTime),
    recentOperationsCount: recentOperations.length,
    errorsByType: { ...operationMetrics.errorsByType },
    errorsByService: { ...operationMetrics.errorsByService },
    circuitBreakers: Object.keys(circuitBreakers).reduce((acc, service) => {
      acc[service] = {
        state: circuitBreakers[service].state,
        failures: circuitBreakers[service].failures,
        lastFailure: circuitBreakers[service].lastFailure,
        lastSuccess: circuitBreakers[service].lastSuccess,
      };
      return acc;
    }, {}),
    lastErrors: [...operationMetrics.lastErrors],
  };
};

/**
 * Reset performance metrics (useful for testing)
 */
export const resetPerformanceMetrics = () => {
  operationMetrics.totalOperations = 0;
  operationMetrics.failedOperations = 0;
  operationMetrics.retryOperations = 0;
  operationMetrics.fallbackOperations = 0;
  operationMetrics.operationTimes = [];
  operationMetrics.errorsByType = {};
  operationMetrics.errorsByService = {};
  operationMetrics.lastErrors = [];

  // Reset circuit breakers
  Object.keys(circuitBreakers).forEach((service) => {
    circuitBreakers[service] = {
      state: CIRCUIT_BREAKER_STATES.CLOSED,
      failures: 0,
      successes: 0,
      lastFailure: null,
      lastSuccess: null,
    };
  });

  Logger.log(
    "info",
    "Performance metrics reset",
    {},
    "FirebasePerformanceMonitor"
  );
};

/**
 * Check Firebase connectivity and authentication status
 */
export const checkFirebaseHealth = async () => {
  const healthStatus = {
    timestamp: new Date().toISOString(),
    connectivity: "unknown",
    authentication: "unknown",
    firestore: "unknown",
    services: {},
  };

  try {
    // Check network connectivity
    if (
      typeof window !== "undefined" &&
      window.navigator?.onLine !== undefined
    ) {
      healthStatus.connectivity = window.navigator.onLine
        ? "online"
        : "offline";
    }

    // Check authentication status
    if (typeof window !== "undefined" && window.$auth) {
      try {
        const user = window.$auth.currentUser;
        healthStatus.authentication = user
          ? "authenticated"
          : "unauthenticated";
        if (user) {
          healthStatus.userId = user.uid;
        }
      } catch (authError) {
        healthStatus.authentication = "error";
        healthStatus.authError = authError.message;
      }
    }

    // Basic Firestore connectivity test
    try {
      const { db } = await import("@/firebase");
      const { collection, query, limit, getDocs } = await import(
        "firebase/firestore"
      );

      const testQuery = query(collection(db, "teachers"), limit(1));
      await getDocs(testQuery);

      healthStatus.firestore = "accessible";
    } catch (firestoreError) {
      healthStatus.firestore = "error";
      healthStatus.firestoreError = firestoreError.message;
    }
  } catch (error) {
    Logger.log(
      "error",
      "Firebase health check failed",
      {
        error: error.message,
        stack: error.stack,
      },
      "FirebasePerformanceMonitor"
    );
  }

  // Add circuit breaker states
  healthStatus.circuitBreakers = Object.keys(circuitBreakers).reduce(
    (acc, service) => {
      acc[service] = circuitBreakers[service].state;
      return acc;
    },
    {}
  );

  Logger.log(
    "info",
    "Firebase health check completed",
    healthStatus,
    "FirebasePerformanceMonitor"
  );

  return healthStatus;
};

/**
 * Log performance summary (useful for debugging)
 */
export const logPerformanceSummary = () => {
  const metrics = getPerformanceMetrics();

  Logger.log(
    "info",
    "Firebase Performance Summary",
    {
      summary: {
        totalOps: metrics.totalOperations,
        successRate: metrics.successRate,
        avgResponseTime: `${metrics.averageResponseTime}ms`,
        recentOps: metrics.recentOperationsCount,
      },
      circuitBreakers: metrics.circuitBreakers,
      topErrors: Object.entries(metrics.errorsByType)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([error, count]) => ({ error, count })),
      lastErrors: metrics.lastErrors.slice(0, 3),
    },
    "FirebasePerformanceMonitor"
  );
};

export default {
  monitorFirebaseOperation,
  getPerformanceMetrics,
  resetPerformanceMetrics,
  checkFirebaseHealth,
  logPerformanceSummary,
};
